const { getElements } = window.BAO.Utils;
class SectionAsync extends window.BAO.CustomElement() {
  static get observedAttributes() {
    return ["section-id", "url", "loading", "cache-key"];
  }

  static get requiredAttributes() {
    return ["section-id"];
  }

  /**
   * Loads a section asynchronously
   */
  connectedCallback() {
    super.connectedCallback();

    if (!this.useCache && this.isLazy && this.isIntersectionObserverSupported) {
      this.intersectionObserverCallback =
        this.intersectionObserverCallback.bind(this);
      this.observer.observe(this);
    } else {
      this.renderSection();
    }
  }

  /**
   * Renders the custom element
   */
  async renderSection() {
    this.isBusy = true;

    window.BAO.dispatchEvent(window.BAO.EVENTS.SECTION_ASYNC.BEFORE_RENDER, {
      sectionId: this.sectionId,
    });

    const cachedHtml = window.sessionStorage.getItem(this.cacheKey);
    const html =
      cachedHtml ||
      (await window.BAO.sectionRenderer.fetchSingle(
        this.sectionId,
        this.sectionUrl,
      ));
    this.sectionHTML = window.BAO.sectionRenderer.getInnerHtml(html);

    window.BAO.dispatchEvent(window.BAO.EVENTS.SECTION_ASYNC.AFTER_RENDER, {
      sectionId: this.sectionId,
    });

    this.isBusy = false;

    if (this.useCache && !cachedHtml) {
      window.sessionStorage.setItem(this.cacheKey, html);
    }
  }

  /**
   * Removes the parent element
   */
  removeParentElement() {
    const elementToRemove = this.parentIdentifier
      ? this.closest(this.parentIdentifier)
      : this;
    elementToRemove.remove();
  }

  /**
   * Calls the initialise function when the custom element is intersecting (visible in the viewport)
   */
  intersectionObserverCallback(entries) {
    const entry = entries?.[0];
    if (!entry?.isIntersecting) return;
    this.renderSection();
    this.observer.disconnect();
  }

  /**
   * @returns {IntersectionObserver} an observer that specifies when an element is intersecting (visible in viewport)
   */
  get observer() {
    // eslint-disable-next-line compat/compat
    return new IntersectionObserver(this.intersectionObserverCallback);
  }

  /**
   * @returns {string} The ID of the section to be rendered
   */
  get sectionId() {
    return this.getAttribute("section-id");
  }

  /**
   * @param {string} html - HTML to be rendered
   */
  set sectionHTML(html) {
    if (!html) {
      /*
       * When there isn't any HTML we want to remove the parent element
       * to ensure we're not showing other elements of the section
       * such as kicker and title
       */
      this.removeParentElement();
      return;
    }

    this.innerHTML = html;
    this.enableDependantElements();
  }

  enableDependantElements() {
    getElements(`[data-await='${this.sectionId}']`).forEach((el) => {
      el.removeAttribute("aria-disabled");
      el.disabled = false;
    });
  }

  /**
   * @returns {string} html - HTML of the section
   */
  get sectionHTML() {
    return this.innerHTML;
  }

  /**
   * @param {boolean} isBusy - Whether or not the element is currently busy
   */
  set isBusy(isBusy) {
    this.setAttribute("aria-busy", String(isBusy));
  }

  /**
   * @returns {boolean} Whether or not the element is currently busy
   */
  get isBusy() {
    return this.getAttribute("aria-busy") === "true";
  }

  /**
   * @returns {string} The URL from which to request the section
   */
  get url() {
    return this.getAttribute("url")?.replace(
      "[[query_parameters]]",
      window.location.search,
    );
  }

  /**
   * @returns {string} The identifier of a parent element (if provided)
   */
  get parentIdentifier() {
    return this?.dataset?.parentIdentifier;
  }

  /**
   * @returns {string} The url, pathname and query parameters
   */
  get sectionUrl() {
    return this.url || `${window.location.pathname}${window.location.search}`;
  }

  /**
   * @returns {boolean} Whether or not this section should load when it's needed instead of at pageload
   */
  get isLazy() {
    return this.getAttribute("loading") === "lazy";
  }

  /**
   * @returns {boolean} Whether or not this section should be cached
   */
  get useCache() {
    return this.hasAttribute("cache-key");
  }

  /**
   * @returns {string} The key to use to cache this section against
   */
  get cacheKey() {
    return this.getAttribute("cache-key");
  }

  /**
   * @returns {boolean} Whether or not IntersectionObserver is supported by the browser
   */
  get isIntersectionObserverSupported() {
    return "IntersectionObserver" in window;
  }
}

if (!customElements.get("section-async")) {
  customElements.define("section-async", SectionAsync);
}
